import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import bulkAudioStore from '../../Mobx/BulkAudioStore';
import { formatError } from '../../Mobx/AudioCatalog';
import BulkRunForm from './BulkRunForm';
import BulkRunPlan from './BulkRunPlan';
import BulkRunJobs from './BulkRunJobs';
import BulkRunJobDetails from './BulkRunJobDetails';
import { PATH_STYLE, SOURCE_FILTER, backupFolderOf, buildRequest, formatNumber, initialForm, isReplaceMode, modeOf, plannedFiles, progressOf, requestKey } from './bulkAudioForm';

/** How often the runs are asked for again while the tab is open: quickly when a run works, slowly when none does */
export const POLL_ACTIVE_MS = 3000;
export const POLL_IDLE_MS = 20000;

/**
 * Refreshes the list of runs while the tab is open (and the browser tab can be seen): quickly while a run works, slowly otherwise.
 * When a run starts (or ends) the loop starts again at once, so the page does not wait for the slow pace to see it.
 * The first call is also the first load of the list.
 */
function useJobsPolling(store, active, working) {
    useEffect(() => {
        if (!active) return undefined;
        const interval = working ? POLL_ACTIVE_MS : POLL_IDLE_MS;
        let stopped = false;
        let timer = null;
        const tick = async () => {
            if (document.visibilityState !== 'hidden') await store.loadJobs();
            if (!stopped) timer = setTimeout(tick, interval);
        };
        tick();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, [active, working, store]);
}

/**
 * The bulk audio runs (SCRUM-1847): the admin chooses the mode, the entries, what is spoken and who speaks, checks the plan (a dry run - nothing is
 * created or replaced), and only then starts a run. The run works on the server, so the page can be closed. In the mode fill-missing the server only
 * creates the files that are missing (a file that exists is never touched); in the mode replace-with-backup it replaces the files that exist, each one
 * after its old version was copied to the backup folder of the run. It never deletes a file.
 *
 * @param {boolean} active  the tab is the one that is open (all the tabs of the page stay mounted, but only the open one talks to the server)
 */
const BulkAudio = observer(({ active = true }) => {
    const store = bulkAudioStore;
    const { options, optionsLoading, optionsError, plan, planKey, planning, planError, starting, startError } = store;
    const activeJob = store.activeJob;

    const [form, setForm] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [notice, setNotice] = useState(null); // { job, nextAfterId } - a run that was just started
    const [openJobId, setOpenJobId] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        if (active) store.loadOptions();
    }, [active, store]);
    useJobsPolling(store, active, Boolean(activeJob));

    // The form starts when the options of the server are here (the dictionaries and the voices come from them)
    useEffect(() => {
        if (options && !form) setForm(initialForm(options));
    }, [options, form]);

    const built = useMemo(() => (form && options ? buildRequest(form, options) : null), [form, options]);
    const key = built?.ok ? requestKey(built.request) : null;
    // The plan on the screen is the plan of the form only while the request of the form is the one that the plan was made for
    const stale = Boolean(plan) && key !== planKey;

    const checkPlan = async () => {
        setShowErrors(true);
        setNotice(null);
        if (!built?.ok) return;
        await store.planRun(built.request, key);
    };

    const startRun = async () => {
        if (!built?.ok || !plan || stale) return;
        const nextAfterId = plan.nextAfterId ?? null;
        const job = await store.startRun(built.request, key, plannedFiles(plan));
        if (job) setNotice({ job, nextAfterId });
    };

    /** The next batch of a filter: the same form, after the last entry that the plan looked at */
    const continueAfter = (afterId) => {
        setForm({ ...form, source: SOURCE_FILTER, afterId });
        setNotice(null);
        formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    };

    if (!options) {
        if (optionsError) {
            return (
                <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={store.loadOptions} disabled={optionsLoading}>נסו שוב</Button>}>
                        <AlertTitle>טעינת האפשרויות של ההרצה המרוכזת נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(optionsError)}</Box>
                    </Alert>
                </Box>
            );
        }
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    const progress = progressOf(activeJob);

    return (
        <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }} data-testid="bulk-audio">
            <Typography variant="h5" sx={{ mb: 0.5 }}>הרצה מרוכזת של אודיו</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                יוצרת בבת אחת את קבצי האודיו של מילים רבות. ברירת המחדל היא ליצור רק קבצים שחסרים: קובץ שכבר קיים לא נוגעים בו.
                כשצריך להחליף קבצים שקיימים (למשל קול שלא נשמע טוב) יש מצב "החלפה עם גיבוי": כל קובץ מועתק לגיבוי לפני שמחליפים אותו, ושום קובץ לא נמחק.
                קודם בודקים תוכנית (בדיקה בלבד, לא נוצר ולא מוחלף כלום ולא נשלחת בקשה לספק הקול), ורק אחרי שהמספרים מתאימים מתחילים.
            </Typography>

            {activeJob && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    data-testid="bulk-active-banner"
                    action={<Button color="inherit" size="small" onClick={() => setOpenJobId(activeJob.id)}>פרטים</Button>}
                >
                    <AlertTitle>הרצה פועלת עכשיו</AlertTitle>
                    <bdi>{formatNumber(progress.finished)} / {formatNumber(progress.total)}</bdi> קבצים. אפשר להתחיל הרצה חדשה כשהיא מסתיימת או נעצרת.
                </Alert>
            )}

            {notice && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    data-testid="bulk-started"
                    action={(
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button color="inherit" size="small" onClick={() => setOpenJobId(notice.job.id)}>פרטים</Button>
                            {notice.nextAfterId && (
                                <Button color="inherit" size="small" onClick={() => continueAfter(notice.nextAfterId)} data-testid="bulk-next-batch">הכנת ההרצה הבאה</Button>
                            )}
                        </Box>
                    )}
                >
                    <AlertTitle>{isReplaceMode(modeOf(notice.job)) ? 'ההחלפה התחילה' : 'ההרצה התחילה'}</AlertTitle>
                    {formatNumber(notice.job.counters?.total)} קבצים{isReplaceMode(modeOf(notice.job)) ? <>. הגיבוי של הקבצים הישנים: <bdi dir="ltr" style={PATH_STYLE}>{backupFolderOf(notice.job, options)}</bdi></> : ''}. ההרצה רצה בשרת: אפשר לעקוב אחריה ברשימה למטה, ואפשר גם לסגור את הדף.
                </Alert>
            )}

            <Box ref={formRef}>
                {form && (
                    <BulkRunForm
                        form={form}
                        setForm={setForm}
                        options={options}
                        errors={built && !built.ok ? built.errors : []}
                        showErrors={showErrors}
                        planning={planning}
                        onPlan={checkPlan}
                    />
                )}
            </Box>

            {planError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="bulk-plan-error">
                    <AlertTitle>בדיקת התוכנית נכשלה</AlertTitle>
                    {planError.errors?.length > 0 && (
                        <Box component="ul" sx={{ m: 0, pl: 3 }} dir="auto">
                            {planError.errors.map((message) => <li key={message}>{message}</li>)}
                        </Box>
                    )}
                    <Box dir="auto">{formatError(planError)}</Box>
                </Alert>
            )}

            <BulkRunPlan
                plan={plan}
                stale={stale}
                options={options}
                starting={starting}
                startError={startError}
                activeJob={activeJob}
                onStart={startRun}
                onContinueAfter={continueAfter}
            />

            <BulkRunJobs onOpen={setOpenJobId} />

            {openJobId && <BulkRunJobDetails key={openJobId} jobId={openJobId} options={options} onClose={() => setOpenJobId(null)} />}
        </Box>
    );
});

export default BulkAudio;
